/** Platform admin: support screen (P9, mockups/admin/support.html). Owned by the support screen; fil mirrors every key. */
export const support = {
  title: "Support",
  subtitle: "Tickets from tenant workspaces and misuse reports from end users. SLA: Urgent 1h · High 4h · Normal 1 business day.",
  impersonationLog: "Impersonation log",
  newTicket: "New ticket",
  stats: {
    open: "Open tickets",
    urgent_one: "{{count}} urgent",
    urgent_other: "{{count}} urgent",
    noUrgent: "None urgent",
    firstResponse: "First response (7d)",
    csat: "CSAT (30d)",
    notTracked: "Not tracked yet",
    abuse: "Abuse reports",
    awaiting: "awaiting review",
  },
  tabs: { label: "Support sections", tickets: "Tickets", abuse: "Abuse reports" },
  list: {
    search: "Search subject, tenant, ticket #",
    views: { label: "Ticket views", open: "Open", mine: "Assigned to me", urgent: "Urgent", closed: "Closed" },
    emptyTitle: "No tickets in this view",
    emptyBody: "Tickets tenants send from their workspace land here. You can also log one yourself.",
    emptySearch: "No tickets match “{{q}}”. Try a tenant name or another word from the subject.",
    clearSearch: "Clear search",
    unassigned: "Unassigned",
    label: "Tickets",
  },
  priority: { urgent: "Urgent", high: "High", normal: "Normal" },
  status: { open: "Open", pending: "Waiting on tenant", eng: "With engineering", closed: "Closed" },
  sla: {
    left: "{{time}} left",
    overdue: "Overdue {{time}}",
    closed: "Closed",
    none: "No SLA",
    hours: "{{h}}h {{m}}m",
    minutes: "{{m}}m",
  },
  detail: {
    back: "All tickets",
    selectTitle: "Select a ticket",
    selectBody: "Pick a ticket from the list to read the thread, reply or escalate.",
    notFoundTitle: "Ticket not found",
    notFoundBody: "It may have been removed, or the link is wrong.",
    meta: "{{tenant}} · assigned to {{assignee}}",
    more: "More ticket actions",
    assignTo: "Assign to {{name}}",
    assignMe: "Assign to me",
    unassign: "Unassign",
    assigned: "Assigned to {{name}}",
    unassigned: "Ticket unassigned",
    raisePriority: "Raise priority",
    priorityRaised: "Priority raised to {{priority}}",
    copyLink: "Copy ticket link",
    linkCopied: "Ticket link copied",
    copyFailed: "Couldn't copy the link",
    impersonate: "Impersonate tenant",
    escalate: "Escalate to engineering",
    close: "Close ticket",
    reopen: "Reopen",
    closed: "{{ref}} closed",
    reopened: "{{ref}} reopened",
    impersonateReason: "{{ref}} · {{subject}}",
  },
  thread: {
    label: "Conversation",
    internalNote: "Internal note",
    staffTeam: "CRUD.IT Support",
    staffFallback: "CRUD.IT staff",
    tenantFallback: "Tenant user",
    empty: "No messages yet. Send the first reply below.",
  },
  composer: {
    modes: { label: "Message type", reply: "Reply to tenant", note: "Internal note" },
    replyLabel: "Reply",
    replyPlaceholder: "Write a reply… the tenant gets an email and an in-app notification.",
    noteLabel: "Internal note",
    notePlaceholder: "Only CRUD.IT staff can see internal notes.",
    send: "Send reply",
    addNote: "Add internal note",
    sent: "Reply sent · tenant notified by email",
    noteAdded: "Internal note added",
    emptyReply: "Write a reply first",
    emptyNote: "Write a note first",
  },
  context: {
    label: "Tenant context",
    plan: "Plan",
    mrr: "MRR",
    messages: "Messages",
    messagesValue: "{{used}} / {{limit}}",
    since: "Customer since",
    status: "Status",
    open: "Open tenant",
  },
  escalate: {
    title: "Escalate to engineering",
    description: "The ticket moves to “With engineering” and the escalation is logged in the audit trail.",
    component: "Component",
    components: {
      rag: "RAG / retrieval",
      ingestion: "Ingestion & OCR",
      llm_gateway: "LLM gateway",
      widget: "Widget / SSE",
      channels: "Channels (Messenger)",
      billing: "Billing",
    },
    severity: "Severity",
    severities: { sev3: "SEV-3", sev2: "SEV-2", sev1: "SEV-1" },
    assignee: "Assign to",
    notes: "Notes for engineering",
    notesPlaceholder: "What you saw, what you tried, and which answers or traces show it.",
    traces: "Attach last 5 traces for this tenant",
    submit: "Escalate",
    done: "Escalated to engineering · {{ref}}",
  },
  create: {
    title: "New ticket",
    description: "Log a ticket on behalf of a tenant workspace. The SLA clock starts now.",
    tenantSearch: "Tenant",
    tenantSearchPlaceholder: "Search tenants by name…",
    tenant: "Matching tenant",
    noTenants: "No tenants match",
    subject: "Subject",
    subjectPlaceholder: "e.g. Onboarding call follow-up",
    subjectRequired: "Add a subject.",
    tenantRequired: "Pick a tenant.",
    priority: "Priority",
    assignee: "Assignee",
    submit: "Create ticket",
    done: "Ticket {{ref}} created",
  },
  abuse: {
    title: "Abuse reports",
    description: "Reported via the “Report this answer” link in widgets, by tenants, or flagged by moderation. Decisions are logged in Audit Logs.",
    columns: { report: "Report", tenant: "Tenant · agent", reason: "Reason", source: "Source", evidence: "Evidence", status: "Status", action: "Action" },
    filter: "Status filter",
    filters: { all: "All", open: "Needs review", reviewing: "In review", actioned: "Actioned", dismissed: "Dismissed" },
    statuses: { open: "Needs review", reviewing: "In review", actioned: "Actioned", dismissed: "Dismissed" },
    reasons: {
      prompt_injection: "Prompt-injection campaign",
      misleading: "Misleading claims",
      credentials: "Collecting credentials",
      spam: "Spam / flooding",
    },
    sources: {
      guardrail: "Guardrail auto-flag",
      end_user: "End-user report",
      moderation: "Moderation",
      rate_limiter: "Rate limiter",
    },
    reportCount_one: "{{count}} report",
    reportCount_other: "{{count}} reports",
    agentLinked: "Agent linked",
    noAgent: "No agent",
    view: "View",
    viewLabel: "View evidence for {{ref}}",
    actions: {
      block_ip: "Block IP",
      dismiss: "Dismiss",
      warn: "Warn tenant",
      suspend_agent: "Suspend agent",
      block_agent_key: "Revoke agent key",
      suspend_tenant: "Suspend tenant",
    },
    actioned: "{{action}} · {{when}}",
    done: {
      block_ip: "IP blocked for this tenant",
      dismiss: "Report dismissed",
      warn: "Warning emailed to tenant owner",
      suspend_agent: "Agent suspended · logged as agent.suspended",
      block_agent_key: "Agent key revoked",
      suspend_tenant: "Tenant suspended",
    },
    emptyTitle: "No abuse reports",
    emptyBody: "Reports from the widget's “Report this answer” link, guardrail flags and moderation show up here for review.",
    emptyFiltered: "No reports with this status. Show every report instead.",
    showAll: "Show all reports",
    openAudit: "Open audit log",
    evidence: {
      title: "Evidence · {{ref}}",
      description: "What the report recorded. Conversation excerpts aren't available from the API yet.",
      summary: "Summary",
      reason: "Reason",
      source: "Source",
      tenant: "Tenant",
      reported: "Reported",
      count: "Reports",
      decision: "Decision",
      undecided: "Not decided yet",
    },
    suspend: {
      title: "Suspend this agent?",
      description: "The widget, web app and Messenger channel will show “This assistant is temporarily unavailable.” The tenant can still log in and export data.",
      message: "Message to tenant owner",
      messageDefault: "Your agent was suspended for violating the Sagot Acceptable Use Policy. Reply to this email to appeal.",
      submit: "Suspend agent",
    },
  },
} as const;
